import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { ACCOUNTS } from '../../api/accounts';
import { resolveVoucherRefs } from '../../api/voucherRefs';
import { downloadCsv } from '../../utils/downloadCsv';

// Fallback only, used until the dynamic LIABILITY-account list below has loaded — the
// real filter is "every active liability account", not this fixed shortlist. A hardcoded
// list here silently misses any one-off dedicated account (e.g. a specific supplier's own
// float, like 2075 Rajabdeen Haji Float) that isn't one of these five shared categories.
const PAYABLE_ACCOUNTS_FALLBACK = [ACCOUNTS.AP_SUPPLIERS, ACCOUNTS.VISA_PAYABLE, ACCOUNTS.AIRLINE_PAYABLE, ACCOUNTS.HOTEL_LAND_PAYABLE, ACCOUNTS.COMMISSION_PAYABLE];

const fmt = (n) => Number(n || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : '-';

const TODAY = new Date();
const daysBetween = (dateStr) => {
  if (!dateStr) return 999;
  return Math.floor((TODAY - new Date(dateStr)) / 86400000);
};
const ageBucket = (days) => {
  if (days <= 30) return '0-30';
  if (days <= 60) return '31-60';
  if (days <= 90) return '61-90';
  return '90+';
};

export default function SupplierLedger() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [txns, setTxns] = useState([]);
  const [showReversed, setShowReversed] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [summaryError, setSummaryError] = useState(null);
  const [error, setError] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortKey, setSortKey] = useState('balance');
  const [sortDir, setSortDir] = useState('desc');
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState('');
  const [payableAccountIds, setPayableAccountIds] = useState(PAYABLE_ACCOUNTS_FALLBACK);
  const [rawSupplierLines, setRawSupplierLines] = useState([]);

  // A sub-supplier (e.g. Arabian Lux, linked under Rajabdeen Haji) is who's actually
  // costed at ticket/invoice time, but the parent is who's actually paid — its balance
  // and transactions belong on the parent's ledger, not its own. Walk parent_supplier_id
  // to the top (cycle-guarded) before grouping anything by party_id.
  const supMap = useMemo(() => {
    const m = {};
    suppliers.forEach(s => { m[s.id] = s; });
    return m;
  }, [suppliers]);
  const topParentOf = useCallback((id) => {
    let cur = id, hops = 0;
    while (supMap[cur]?.parent_supplier_id && hops < 10) { cur = supMap[cur].parent_supplier_id; hops++; }
    return cur;
  }, [supMap]);

  const fetchAll = async (table, query) => {
    const PAGE = 1000;
    let all = [], from = 0, done = false;
    while (!done) {
      const { data, error } = await query(supabase.from(table)).range(from, from + PAGE - 1);
      if (error || !data?.length) break;
      all = all.concat(data);
      if (data.length < PAGE) done = true;
      else from += PAGE;
    }
    return all;
  };

  useEffect(() => {
    fetchAll('suppliers', q => q.select('id,supplier_code,name,supplier_type,parent_supplier_id,is_active').order('name'))
      .then(data => {
        setSuppliers(data || []);
        const preselectId = searchParams.get('supplier');
        if (preselectId) {
          // A sub-supplier (e.g. Arabian Lux) has no ledger of its own anymore — its
          // balance lives on its parent (Rajabdeen Haji), so a deep link to the child
          // should land on the parent instead of an always-empty page.
          const direct = (data || []).find(s => s.id === preselectId);
          const match = direct?.parent_supplier_id ? (data || []).find(s => s.id === direct.parent_supplier_id) || direct : direct;
          if (match) setSelectedSupplier(match);
        }
      });
    supabase.from('branches').select('id, name, code').eq('is_active', true).order('name').then(({ data }) => setBranches(data || []));
    supabase.from('chart_of_accounts').select('id, account_types!inner(category)').eq('is_active', true).eq('account_types.category', 'LIABILITY')
      .then(({ data }) => { if (data?.length) setPayableAccountIds(data.map(a => a.id)); });
  }, [searchParams]);

  // Fetched once (independent of the branch filter) — a payment's branch_id only records
  // which branch's till/bank account physically paid, not whose debt it settled, so it is
  // never a valid basis for a per-branch "Balance Payable." The real, company-wide balance
  // must always be computed from the FULL history; only the branch-scoped "Cost Incurred"
  // figure (derived below, credit-side only) is meaningful to slice by branch.
  useEffect(() => {
    async function loadSummary() {
      setLoadingSummary(true);
      setSummaryError(null);
      try {
        const lines = await fetchAll('journal_lines', q => q
          .select('base_debit, base_credit, party_id, branch_id, journal_entries!inner(entry_date, status, is_reversed, reversal_of)')
          .in('account_id', payableAccountIds)
          .eq('party_type', 'supplier')
          .eq('journal_entries.status', 'POSTED'));
        setRawSupplierLines(lines || []);
      } catch (e) {
        setSummaryError(e.message);
      } finally {
        setLoadingSummary(false);
      }
    }
    loadSummary();
  }, [payableAccountIds]);

  const summaryData = useMemo(() => {
    const map = {};
    // A reversed entry and its reversal net to zero — exclude both from gross
    // Cost Incurred / Paid, or a corrected mistake inflates these totals.
    rawSupplierLines.filter(l => !l.journal_entries?.is_reversed && !l.journal_entries?.reversal_of).forEach(l => {
      const id = l.party_id ? topParentOf(l.party_id) : l.party_id;
      if (!id) return;
      if (!map[id]) map[id] = { credit: 0, debit: 0, branchCredit: 0, oldestUnpaid: null };
      const dr = Number(l.base_debit || 0);
      const cr = Number(l.base_credit || 0);
      map[id].debit += dr;
      map[id].credit += cr;
      // Cost Incurred, scoped to the selected branch — credit-side only. Debit (payment)
      // lines are deliberately never filtered by branch here; see comment above.
      if (!branchFilter || l.branch_id === branchFilter) map[id].branchCredit += cr;
      const date = l.journal_entries?.entry_date;
      // Track oldest cost-booking date for aging
      if (cr > 0 && date && (!map[id].oldestUnpaid || date < map[id].oldestUnpaid))
        map[id].oldestUnpaid = date;
    });
    return map;
  }, [rawSupplierLines, branchFilter, topParentOf]);

  const summaryRows = useMemo(() => {
    const rows = suppliers
      .filter(s => !s.parent_supplier_id && s.is_active !== false) // sub-suppliers roll up into their parent's row; merged/inactive suppliers get none
      .map(s => {
        const d = summaryData[s.id] || null;
        if (!d) return null;
        const balance = d.credit - d.debit; // AP: credit = owe more, debit = paid — always company-wide, never branch-filtered
        const bk = balance > 0 ? ageBucket(daysBetween(d.oldestUnpaid || '2000-01-01')) : null;
        return {
          ...s,
          credit: d.credit,
          debit: d.debit,
          costIncurred: d.branchCredit,
          balance,
          b0_30:   bk === '0-30'  ? balance : 0,
          b31_60:  bk === '31-60' ? balance : 0,
          b61_90:  bk === '61-90' ? balance : 0,
          b90plus: bk === '90+'   ? balance : 0,
        };
      })
      .filter(Boolean);

    rows.sort((a, b) => {
      let av = a[sortKey] ?? 0;
      let bv = b[sortKey] ?? 0;
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [suppliers, summaryData, sortKey, sortDir]);

  const grandTotals = useMemo(() => summaryRows.reduce((acc, r) => ({
    credit: acc.credit + r.credit,
    debit: acc.debit + r.debit,
    costIncurred: acc.costIncurred + r.costIncurred,
    balance: acc.balance + r.balance,
    b0_30: acc.b0_30 + r.b0_30,
    b31_60: acc.b31_60 + r.b31_60,
    b61_90: acc.b61_90 + r.b61_90,
    b90plus: acc.b90plus + r.b90plus,
  }), { credit: 0, debit: 0, costIncurred: 0, balance: 0, b0_30: 0, b31_60: 0, b61_90: 0, b90plus: 0 }), [summaryRows]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };
  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <span style={{ opacity: 0.3, marginLeft: 3 }}>↕</span>;
    return <span style={{ marginLeft: 3, color: 'var(--primary)' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const loadLedger = useCallback(async (supplier) => {
    if (!supplier) return;
    setLoadingDetail(true);
    setError(null);
    setTxns([]);
    try {
      const typeLabel = (rt) => {
        switch (rt) {
          case 'TICKET_COST':    return 'Ticket';
          case 'VISA_COST':      return 'Visa';
          case 'BOOKING_COST':   return 'Package';
          case 'PAYMENT_VOUCHER': return 'Payment';
          default: return rt || 'Journal';
        }
      };

      // Include any sub-suppliers linked under this one (e.g. Arabian Lux under Rajabdeen
      // Haji) — their transactions are part of this same real payable, just tagged to the
      // child at entry time, so they belong in this same ledger, labeled with their origin.
      const childSuppliers = suppliers.filter(s => s.parent_supplier_id === supplier.id);
      const childIds = childSuppliers.map(s => s.id);
      const allIds = [supplier.id, ...childIds];
      const childMap = {};
      childSuppliers.forEach(s => { childMap[s.id] = s; });

      // A busy supplier (IATA: 1,546 lines) blows past PostgREST's 1000-row default cap —
      // with no explicit order, the truncated page isn't even guaranteed to be the oldest
      // or newest rows, so a recent entry can simply vanish from this ledger. fetchAll()
      // pages through everything instead of trusting a single unbounded select.
      // Scoped to the same dynamic liability-account list the summary balance already uses
      // (payableAccountIds) — without this, an unrelated asset posting tagged to the same
      // supplier (e.g. Supplier Deposits, 1040, for a prepaid airline advance) blends into
      // this "payable" ledger and makes a real AP credit look cancelled out by a debit that
      // has nothing to do with what's owed to them.
      //
      // Deliberately NOT filtered by branch — a payment's branch_id only says which branch's
      // till/bank account paid it, not whose debt it settled, so a single supplier's true
      // ledger and running balance must always reflect its complete, company-wide history
      // regardless of which branch is selected on the summary page.
      const lines = await fetchAll('journal_lines', (q) => {
        let r = q
          .select('id, party_id, debit_amount, credit_amount, base_debit, base_credit, description, journal_entries!inner(id, entry_date, entry_number, description, reference_type, reference_id, status, exchange_rate, is_reversed, reversal_of, created_at, currencies(code))')
          .eq('party_type', 'supplier')
          .in('party_id', allIds)
          .in('account_id', payableAccountIds)
          .eq('journal_entries.status', 'POSTED')
          .order('entry_date', { referencedTable: 'journal_entries', ascending: true });
        if (dateFrom) r = r.gte('journal_entries.entry_date', dateFrom);
        if (dateTo)   r = r.lte('journal_entries.entry_date', dateTo);
        return r;
      });

      // A JE number is internal — show the PV/RV behind each row instead (a supplier refund
      // comes in on an RV), looked up in one batch.
      const voucherRefs = await resolveVoucherRefs(lines || []);

      // Invoice-backed lines (INVOICE_TICKET/INVOICE_VISA/INVOICE_BOOKING) should show the
      // invoice number (e.g. TLS-26-0012), not the underlying journal entry number.
      const invIds = [...new Set((lines || [])
        .filter(l => ['INVOICE_TICKET', 'INVOICE_VISA', 'INVOICE_BOOKING', 'invoice'].includes(l.journal_entries?.reference_type) && l.journal_entries?.reference_id)
        .map(l => l.journal_entries.reference_id))];
      let invNumberMap = new Map();
      if (invIds.length) {
        const { data: invs } = await supabase.from('invoices').select('id, invoice_number').in('id', invIds);
        invNumberMap = new Map((invs || []).map(i => [i.id, i.invoice_number]));
      }

      const rows = (lines || []).map(l => {
        const je = l.journal_entries;
        const refType = je?.reference_type;
        const refId   = je?.reference_id;
        let drillPath = null;
        // INVOICE_TICKET/INVOICE_VISA/INVOICE_BOOKING are what InvoiceNew.js actually posts
        // today (both the revenue leg and the supplier cost leg share the same reference_type/
        // reference_id = the invoice itself) — TICKET_COST/VISA_COST/BOOKING_COST are legacy,
        // barely-used types from the old quotation-conversion flow.
        if      (refType === 'INVOICE_TICKET' || refType === 'INVOICE_VISA' || refType === 'INVOICE_BOOKING') drillPath = refId ? `/invoices/${refId}` : null;
        else if ((refType === 'invoice') && refId) drillPath = `/invoices/${refId}`;
        else if (refType === 'BILL'           && refId) drillPath = `/bills/${refId}`;
        else if (refType === 'TICKET_COST'    && refId) drillPath = `/tickets/${refId}`;
        else if (refType === 'VISA_COST'      && refId) drillPath = `/visas/${refId}`;
        else if (refType === 'BOOKING_COST'   && refId) drillPath = `/bookings/${refId}`;
        else if (refType === 'package'        && refId) drillPath = `/packages/${refId}`;
        else if (refType === 'PAYMENT_VOUCHER' && refId) drillPath = `/vouchers/payment/${refId}`;
        else if (refType === 'RECEIPT_VOUCHER' && refId) drillPath = `/vouchers/receipt/${refId}`;
        else if (je?.id) drillPath = `/journals/${je.id}`;
        const currCode = je?.currencies?.code || 'LKR';
        const fxRate   = Number(je?.exchange_rate || 1);
        const origDebit  = Number(l.debit_amount  || 0);
        const origCredit = Number(l.credit_amount || 0);
        return {
          txn_id:    l.id,
          txn_date:  je?.entry_date,
          txn_type:  typeLabel(refType),
          sub_supplier: l.party_id !== supplier.id ? (childMap[l.party_id]?.name || null) : null,
          ref_number: voucherRefs.get(je?.id)
            || (['INVOICE_TICKET', 'INVOICE_VISA', 'INVOICE_BOOKING', 'invoice'].includes(refType) && refId && invNumberMap.get(refId))
            || je?.entry_number || '',
          narration: l.description || je?.description || '',
          debit:     Number(l.base_debit  || 0),
          credit:    Number(l.base_credit || 0),
          orig_debit:  origDebit,
          orig_credit: origCredit,
          curr_code: currCode,
          fx_rate:   fxRate,
          ref_type:  refType,
          ref_id:    refId,
          // Same-day order: costs/adjustments first, then payments, then by posting time —
          // sorting on date alone left same-day rows in arbitrary order, so the running
          // balance never showed the day's position before its payments.
          sort_key:  `${je?.entry_date || ''}|${refType === 'PAYMENT_VOUCHER' ? 1 : 0}|${je?.created_at || ''}`,
          drill_path: drillPath,
          is_reversed_pair: !!(je?.is_reversed || je?.reversal_of),
        };
      });

      // For BOOKING_COST lines, pull original currency from booking_suppliers
      const bookingCostIds = rows.filter(r => r.ref_type === 'BOOKING_COST').map(r => r.ref_id).filter(Boolean);
      let bsMap = {};
      if (bookingCostIds.length) {
        const { data: bsRows } = await supabase.from('booking_suppliers')
          .select('booking_id, cost_amount, exchange_rate, currency_id, currencies(code)')
          .in('supplier_id', allIds)
          .in('booking_id', bookingCostIds);
        (bsRows || []).forEach(bs => { bsMap[bs.booking_id] = bs; });
      }
      rows.forEach(r => {
        if (r.ref_type === 'BOOKING_COST' && bsMap[r.ref_id]) {
          const bs = bsMap[r.ref_id];
          const fx = parseFloat(bs.exchange_rate || 1);
          const lkrAmt = parseFloat(bs.cost_amount || 0);
          r.curr_code = bs.currencies?.code || 'LKR';
          r.fx_rate   = fx;
          r.orig_credit = fx !== 1 ? lkrAmt / fx : lkrAmt;
          r.orig_debit  = 0;
        }
      });

      rows.sort((a, b) => (a.sort_key || '').localeCompare(b.sort_key || ''));
      setTxns(rows);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingDetail(false);
    }
  }, [dateFrom, dateTo, suppliers, payableAccountIds]);

  useEffect(() => {
    if (selectedSupplier) loadLedger(selectedSupplier);
  }, [selectedSupplier, loadLedger]);

  const reversedCount = txns.filter(t => t.is_reversed_pair).length;
  const visibleTxns = useMemo(() => {
    const filtered = showReversed ? txns : txns.filter(t => !t.is_reversed_pair);
    let running = 0;
    return filtered.map(r => { running += r.credit - r.debit; return { ...r, running_balance: running }; });
  }, [txns, showReversed]);
  const totalCredit = visibleTxns.reduce((s, r) => s + r.credit, 0);
  const totalDebit  = visibleTxns.reduce((s, r) => s + r.debit,  0);
  const balance     = totalCredit - totalDebit;

  // Per-entity balance breakdown — the top-line balance above is the true combined
  // payable, but it should still be possible to see how much of it is Rajabdeen Haji's
  // own direct activity vs. each linked sub-supplier's (Arabian Lux etc.), not just a
  // per-row badge in the transaction table.
  const entityBreakdown = useMemo(() => {
    const map = {};
    visibleTxns.forEach(t => {
      const key = t.sub_supplier || (selectedSupplier?.name || 'Direct');
      if (!map[key]) map[key] = { name: key, credit: 0, debit: 0 };
      map[key].credit += t.credit;
      map[key].debit += t.debit;
    });
    return Object.values(map).map(r => ({ ...r, balance: r.credit - r.debit })).sort((a, b) => b.balance - a.balance);
  }, [visibleTxns, selectedSupplier]);

  const handleExport = () => {
    if (!visibleTxns.length) return;
    downloadCsv([
      ['Date', 'Type', 'Sub-Supplier', 'Reference', 'Narration', 'Payment (Dr)', 'Purchase (Cr)', 'Balance (LKR)'],
      ...visibleTxns.map(t => [fmtDate(t.txn_date), t.txn_type, t.sub_supplier || '', t.ref_number, t.narration, t.debit.toFixed(2), t.credit.toFixed(2), t.running_balance.toFixed(2)])
    ], `supplier-ledger-${selectedSupplier?.supplier_code}.csv`);
  };

  const exportSummary = () => {
    downloadCsv([
      ['Supplier Code', 'Supplier Name', 'Type', 'Total Cost', 'Total Paid', 'Balance Payable', '0-30 Days', '31-60 Days', '61-90 Days', '90+ Days'],
      ...summaryRows.map(r => [r.supplier_code, r.name, r.supplier_type, r.credit.toFixed(2), r.debit.toFixed(2), r.balance.toFixed(2), r.b0_30.toFixed(2), r.b31_60.toFixed(2), r.b61_90.toFixed(2), r.b90plus.toFixed(2)])
    ], 'supplier-payables-summary.csv');
  };

  const typeBadge = (type) => {
    const styles = {
      Payment: { bg: '#dcfce7', color: '#15803d' },
      Ticket:  { bg: '#dbeafe', color: '#1d4ed8' },
      Visa:    { bg: '#ede9fe', color: '#7c3aed' },
      Package: { bg: '#fef3c7', color: '#92400e' },
    };
    const c = styles[type] || { bg: '#f1f5f9', color: '#475569' };
    return <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: c.bg, color: c.color }}>{type}</span>;
  };

  // ── DETAIL VIEW ──
  if (selectedSupplier) {
    const filteredLeft = suppliers.filter(s => !s.parent_supplier_id && s.is_active !== false).filter(s =>
      s.name?.toLowerCase().includes(supplierSearch.toLowerCase()) ||
      s.supplier_code?.toLowerCase().includes(supplierSearch.toLowerCase())
    );
    return (
      <div style={{ display: 'flex', height: 'calc(100vh - 80px)' }}>
        {/* Left panel */}
        <div style={{ width: 240, borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
          <div style={{ padding: '12px 10px 8px', borderBottom: '1px solid var(--border)' }}>
            <button className="btn btn-secondary btn-sm" style={{ width: '100%', marginBottom: 8 }} onClick={() => setSelectedSupplier(null)}>← All Suppliers</button>
            <input className="input" placeholder="Search..." value={supplierSearch} onChange={e => setSupplierSearch(e.target.value)} style={{ fontSize: 12 }} />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filteredLeft.map(s => {
              const d = summaryData[s.id];
              const bal = d ? d.credit - d.debit : 0;
              return (
                <div key={s.id} onClick={() => setSelectedSupplier(s)} style={{
                  padding: '9px 10px', cursor: 'pointer', borderBottom: '1px solid var(--border)',
                  background: selectedSupplier?.id === s.id ? '#eff6ff' : 'transparent',
                  borderLeft: selectedSupplier?.id === s.id ? '3px solid var(--primary)' : '3px solid transparent',
                }}>
                  <div style={{ fontSize: 12, fontWeight: 500 }}>{s.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between' }}>
                    <span>{s.supplier_code}</span>
                    {bal > 0 && <span style={{ color: '#dc2626', fontWeight: 600 }}>{fmt(bal)}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right panel */}
        <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 20 }}>{selectedSupplier.name}</h2>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{selectedSupplier.supplier_code} · {selectedSupplier.supplier_type}</div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="date" className="input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ fontSize: 12, width: 140 }} />
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>to</span>
              <input type="date" className="input" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ fontSize: 12, width: 140 }} />
              <button className="btn btn-secondary btn-sm" onClick={() => loadLedger(selectedSupplier)}>Apply</button>
              <button className="btn btn-secondary btn-sm" onClick={handleExport}>CSV</button>
              <button className="btn btn-primary btn-sm" onClick={() => navigate('/vouchers/payment/new')}>+ Payment</button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'Total Purchases', value: fmt(totalCredit), color: '#dc2626' },
              { label: 'Total Paid',      value: fmt(totalDebit),  color: '#16a34a' },
              { label: balance >= 0 ? 'Balance Payable' : 'Overpaid', value: fmt(Math.abs(balance)), color: balance >= 0 ? '#dc2626' : '#16a34a' },
            ].map(c => (
              <div key={c.label} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px' }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{c.label}</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: c.color }}>LKR {c.value}</div>
              </div>
            ))}
          </div>

          {entityBreakdown.length > 1 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Balance by Sub-Category</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {entityBreakdown.map(e => (
                  <div key={e.name} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', minWidth: 160 }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{e.name}</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: e.balance >= 0 ? '#dc2626' : '#16a34a' }}>
                      LKR {fmt(Math.abs(e.balance))} <span style={{ fontSize: 10 }}>{e.balance >= 0 ? 'Cr' : 'Dr'}</span>
                    </div>
                  </div>
                ))}
                <div style={{ background: '#f8fafc', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', minWidth: 160 }}>
                  <div style={{ fontSize: 12, fontWeight: 700 }}>Total (combined)</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: balance >= 0 ? '#dc2626' : '#16a34a' }}>
                    LKR {fmt(Math.abs(balance))} <span style={{ fontSize: 10 }}>{balance >= 0 ? 'Cr' : 'Dr'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {error && <div style={{ color: '#dc2626', marginBottom: 12, fontSize: 13 }}>Error: {error}</div>}

          {loadingDetail ? (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>Loading transactions...</div>
          ) : txns.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>No transactions found for this supplier</div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  Click a Ticket, Visa, or Package row to open the source document.
                </div>
                {reversedCount > 0 && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={showReversed} onChange={e => setShowReversed(e.target.checked)} />
                    Show {reversedCount} reversed/corrected entr{reversedCount === 1 ? 'y' : 'ies'} (excluded from totals above)
                  </label>
                )}
              </div>
              <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid var(--border)' }}>
                      {['Date', 'Type', 'Sub-Supplier', 'Reference', 'Narration', 'Curr', 'FX Rate', 'Orig. Amount', 'Payment (Dr)', 'Purchase (Cr)', 'Balance'].map(h => (
                        <th key={h} style={{ padding: '10px 12px', textAlign: ['FX Rate', 'Orig. Amount', 'Payment (Dr)', 'Purchase (Cr)', 'Balance'].includes(h) ? 'right' : 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleTxns.map((t, i) => (
                      <tr key={t.txn_id + i}
                        onClick={() => t.drill_path && navigate(t.drill_path)}
                        style={{ borderBottom: '1px solid var(--border)', background: i % 2 ? '#fafafa' : 'white', cursor: t.drill_path ? 'pointer' : 'default', opacity: t.is_reversed_pair ? 0.55 : 1, fontStyle: t.is_reversed_pair ? 'italic' : 'normal' }}
                        onMouseEnter={e => { if (t.drill_path) e.currentTarget.style.background = '#eff6ff'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = i % 2 ? '#fafafa' : 'white'; }}
                      >
                        <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>{fmtDate(t.txn_date)}</td>
                        <td style={{ padding: '9px 12px' }}>{typeBadge(t.txn_type)}</td>
                        <td style={{ padding: '9px 12px', fontSize: 12 }}>
                          {t.sub_supplier ? <span style={{ padding: '2px 7px', borderRadius: 4, background: '#f1f5f9', color: '#475569', fontWeight: 600, fontSize: 11 }}>{t.sub_supplier}</span> : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                        </td>
                        <td style={{ padding: '9px 12px', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{t.ref_number || '—'}</td>
                        <td style={{ padding: '9px 12px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: t.drill_path ? 'var(--primary)' : 'var(--text-muted)', fontWeight: t.drill_path ? 500 : 400 }}>
                          {t.narration || '—'}{t.drill_path && <span style={{ fontSize: 10, marginLeft: 6, opacity: 0.6 }}>→</span>}
                        </td>
                        <td style={{ padding: '9px 12px', fontSize: 11, color: 'var(--text-muted)' }}>{t.curr_code || 'LKR'}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', fontSize: 11, color: 'var(--text-muted)' }}>{t.fx_rate ? Number(t.fx_rate).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '1.00'}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', fontSize: 11, color: 'var(--text-muted)' }}>
                          {t.orig_credit > 0 ? fmt(t.orig_credit) : t.orig_debit > 0 ? fmt(t.orig_debit) : '—'}
                        </td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', color: t.debit > 0 ? '#16a34a' : 'var(--text-muted)' }}>{t.debit > 0 ? fmt(t.debit) : '—'}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', color: t.credit > 0 ? '#dc2626' : 'var(--text-muted)' }}>{t.credit > 0 ? fmt(t.credit) : '—'}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 600, color: t.running_balance >= 0 ? '#dc2626' : '#16a34a' }}>
                          {fmt(Math.abs(t.running_balance))} <span style={{ fontSize: 10 }}>{t.running_balance >= 0 ? 'Cr' : 'Dr'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid var(--border)', background: '#f8fafc', fontWeight: 700 }}>
                      <td colSpan={8} style={{ padding: '10px 12px' }}>Totals</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: '#16a34a' }}>{fmt(totalDebit)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: '#dc2626' }}>{fmt(totalCredit)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: balance >= 0 ? '#dc2626' : '#16a34a' }}>
                        {fmt(Math.abs(balance))} <span style={{ fontSize: 10 }}>{balance >= 0 ? 'Cr' : 'Dr'}</span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── SUMMARY VIEW (default) ──
  const thS = (col, align = 'right') => ({
    padding: '10px 12px', textAlign: align, fontWeight: 600, fontSize: 12, color: 'var(--text-muted)',
    cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
    background: sortKey === col ? '#f0f5ff' : '#f8fafc',
  });

  const filteredSummary = summaryRows.filter(r =>
    r.name?.toLowerCase().includes(supplierSearch.toLowerCase()) ||
    r.supplier_code?.toLowerCase().includes(supplierSearch.toLowerCase()) ||
    r.supplier_type?.toLowerCase().includes(supplierSearch.toLowerCase())
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20 }}>Supplier Ledger</h2>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Supplier balances & aging — click any row to view full ledger</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select className="form-input" style={{ fontSize: 13 }} value={branchFilter} onChange={e => { setBranchFilter(e.target.value); setSelectedSupplier(null); }}>
            <option value="">All Branches</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <button className="btn btn-secondary btn-sm" onClick={exportSummary}>Export CSV</button>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/vouchers/payment/new')}>+ Payment</button>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <input className="form-input" placeholder="Search by supplier name, code or type..." value={supplierSearch} onChange={e => setSupplierSearch(e.target.value)} style={{ maxWidth: 400 }} />
        {branchFilter && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
            Branch filter narrows <strong>Cost Incurred</strong> to this branch's own bookings only. <strong>Balance Payable</strong> stays company-wide — a payment isn't tied to one branch's share of what's owed, so it can't be split by branch.
          </div>
        )}
      </div>

      {/* Grand totals */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px' }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Total Payable (all branches)</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#dc2626' }}>LKR {fmt(grandTotals.balance)}</div>
        </div>
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px' }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Cost Incurred{branchFilter ? ` (${branches.find(b => b.id === branchFilter)?.name || 'this branch'})` : ' (all branches)'}</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#1d4ed8' }}>LKR {fmt(grandTotals.costIncurred)}</div>
        </div>
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px' }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Suppliers with Balance</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#7c3aed' }}>{summaryRows.filter(r => r.balance > 0).length}</div>
        </div>
      </div>

      {/* Aging buckets */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 20 }}>
        {[
          { label: '0–30 Days (Current)', value: grandTotals.b0_30,   color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
          { label: '31–60 Days',          value: grandTotals.b31_60,  color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
          { label: '61–90 Days',          value: grandTotals.b61_90,  color: '#ea580c', bg: '#fff7ed', border: '#fed7aa' },
          { label: '90+ Days (Overdue)',   value: grandTotals.b90plus, color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
        ].map(c => (
          <div key={c.label} style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: c.color, marginBottom: 4 }}>{c.label}</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: c.color }}>LKR {fmt(c.value)}</div>
          </div>
        ))}
      </div>

      {summaryError && <div style={{ color: '#dc2626', marginBottom: 12, fontSize: 13 }}>Error loading balances: {summaryError}</div>}

      <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
        {loadingSummary ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>Loading supplier balances...</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={thS('supplier_code', 'left')} onClick={() => handleSort('supplier_code')}>Code <SortIcon col="supplier_code" /></th>
                  <th style={thS('name', 'left')} onClick={() => handleSort('name')}>Supplier Name <SortIcon col="name" /></th>
                  <th style={thS('supplier_type', 'left')} onClick={() => handleSort('supplier_type')}>Type <SortIcon col="supplier_type" /></th>
                  <th style={thS('balance')} onClick={() => handleSort('balance')}>Balance Payable <SortIcon col="balance" /></th>
                  <th style={thS('b0_30')} onClick={() => handleSort('b0_30')}>0–30d <SortIcon col="b0_30" /></th>
                  <th style={thS('b31_60')} onClick={() => handleSort('b31_60')}>31–60d <SortIcon col="b31_60" /></th>
                  <th style={thS('b61_90')} onClick={() => handleSort('b61_90')}>61–90d <SortIcon col="b61_90" /></th>
                  <th style={thS('b90plus')} onClick={() => handleSort('b90plus')}>90+d <SortIcon col="b90plus" /></th>
                </tr>
              </thead>
              <tbody>
                {filteredSummary.map((r, i) => (
                  <tr key={r.id} onClick={() => setSelectedSupplier(r)}
                    style={{ borderBottom: '1px solid var(--border)', background: i % 2 ? '#fafafa' : 'white', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 ? '#fafafa' : 'white'}>
                    <td style={{ padding: '9px 12px', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{r.supplier_code}</td>
                    <td style={{ padding: '9px 12px', fontWeight: 500 }}>{r.name}</td>
                    <td style={{ padding: '9px 12px', color: 'var(--text-muted)', fontSize: 12 }}>{r.supplier_type}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 700, color: r.balance > 0 ? '#dc2626' : '#16a34a' }}>
                      {fmt(Math.abs(r.balance))} <span style={{ fontSize: 10 }}>{r.balance > 0 ? 'Cr' : 'Dr'}</span>
                    </td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', color: '#16a34a' }}>{r.b0_30   > 0 ? fmt(r.b0_30)   : '—'}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', color: '#d97706' }}>{r.b31_60  > 0 ? fmt(r.b31_60)  : '—'}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', color: '#ea580c' }}>{r.b61_90  > 0 ? fmt(r.b61_90)  : '—'}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: r.b90plus > 0 ? 600 : 400, color: r.b90plus > 0 ? '#dc2626' : 'var(--text-muted)' }}>
                      {r.b90plus > 0 ? fmt(r.b90plus) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--border)', background: '#f8fafc', fontWeight: 700 }}>
                  <td colSpan={3} style={{ padding: '10px 12px' }}>Totals ({filteredSummary.length} suppliers)</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#dc2626' }}>{fmt(grandTotals.balance)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#16a34a' }}>{fmt(grandTotals.b0_30)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#d97706' }}>{fmt(grandTotals.b31_60)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#ea580c' }}>{fmt(grandTotals.b61_90)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#dc2626' }}>{fmt(grandTotals.b90plus)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
